/**
 * Shipment Notification Service
 * 
 * Provides functionality to send shipment notifications to sales representatives.
 * Handles email notifications and in-app notifications.
 * 
 * @module ShipmentNotificationService
 */

import {
  collection,
  doc,
  setDoc,
  Timestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Shipment, ShipmentNotification, ShipmentGroup } from '../@types/shipment';
import { EmailService } from './EmailService';
import { ShipmentService } from './ShipmentService';
import { TrackingLinkService } from './TrackingLinkService';
import { NotificationServiceV2 } from './NotificationServiceV2';
import { ENV } from '../config/env';

/**
 * Shipment Notification Service
 * Handles sending notifications to sales reps
 */
export class ShipmentNotificationService {
  private static readonly NOTIFICATIONS_COLLECTION = 'shipment_notifications';
  private static readonly USERS_COLLECTION = 'users';

  /**
   * Send shipments to sales reps by sales group
   * 
   * @param shipments - Array of shipments to send
   * @param salesGroup - Sales group identifier
   * @param managerId - Manager user ID sending the shipments
   * @returns Promise resolving when notifications are sent
   */
  static async sendShipmentsToSalesReps(
    shipments: Shipment[],
    salesGroup: string,
    managerId: string
  ): Promise<void> {
    try {
      console.log('[ShipmentNotificationService] Sending shipments to sales reps:', {
        shipmentCount: shipments.length,
        salesGroup,
        managerId
      });

      if (shipments.length === 0) {
        throw new Error('No shipments to send');
      }

      // Find sales reps for this sales group
      const salesReps = await this.getSalesRepsBySalesGroup(salesGroup);
      
      if (salesReps.length === 0) {
        console.warn('[ShipmentNotificationService] No sales reps found for sales group:', salesGroup);
        throw new Error(`No sales representatives found for sales group: ${salesGroup}`);
      }

      console.log('[ShipmentNotificationService] Found', salesReps.length, 'sales reps for group:', salesGroup);

      // Group shipments by tracking ID
      const shipmentGroups = ShipmentService.groupShipmentsByTrackingId(shipments);

      // Send notifications to each sales rep
      const salesRepIds: string[] = [];
      
      for (const salesRep of salesReps) {
        try {
          // Generate email content
          const emailContent = this.generateEmailContent(shipmentGroups, salesGroup);
          const emailSubject = `New Shipments Assigned - ${salesGroup}`;

          // Send email
          if (salesRep.email) {
            await EmailService.sendEmail({
              to: salesRep.email,
              subject: emailSubject,
              body: emailContent
            });
            console.log('[ShipmentNotificationService] Sent email to:', salesRep.email);
          }

          // Copy shipments to assigned_shipments subcollection for this sales rep
          for (const shipment of shipments) {
            try {
              await ShipmentService.createAssignedShipment(salesRep.id, shipment, managerId);
              console.log('[ShipmentNotificationService] Created assigned shipment copy for sales rep:', salesRep.id, 'shipment:', shipment.id);
            } catch (error: any) {
              console.error('[ShipmentNotificationService] Error creating assigned shipment:', {
                salesRepId: salesRep.id,
                shipmentId: shipment.id,
                error: error.message
              });
              // Continue with other shipments even if one fails
            }

            // Create in-app notifications for each shipment
            await this.createInAppNotification(shipment.id, salesRep.id, managerId);
          }

          // Create a single notification for the sales rep about new shipments assigned
          // Only create one notification per sales rep, not per shipment
          try {
            const shipmentCount = shipments.length;
            const notificationMessage = shipmentCount === 1 
              ? `You have been assigned 1 new shipment`
              : `You have been assigned ${shipmentCount} new shipments`;
            
            const notificationId = await NotificationServiceV2.createNotification({
              userId: salesRep.id,
              type: 'SHIPMENT_ASSIGNED',
              title: 'New Shipments Assigned',
              message: notificationMessage,
              priority: 'MEDIUM',
              metadata: {
                salesGroup,
                shipmentCount,
                link: '/shipments'
              }
            });

            // Send push notification via Firebase Functions (non-blocking)
            // Push notifications are optional and failures won't break shipment assignment
            const sendPushNotification = async () => {
              try {
                const functionsUrl = ENV.FIREBASE_FUNCTIONS_URL || 
                  `https://us-central1-${ENV.FIREBASE_PROJECT_ID || 'mckenzieportal-41c3d'}.cloudfunctions.net`;
                const functionUrl = `${functionsUrl}/sendPushNotification`;
                
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000);
                
                await fetch(functionUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    userId: salesRep.id,
                    title: 'New Shipments Assigned',
                    body: notificationMessage,
                    data: {
                      notificationId,
                      type: 'SHIPMENT_ASSIGNED',
                      deepLink: '/shipments',
                      priority: 'normal'
                    }
                  }),
                  signal: controller.signal
                });
                
                clearTimeout(timeoutId);
              } catch (err: any) {
                // Silently fail for push notifications - they're optional
                if (err.name !== 'AbortError') {
                  console.warn('[ShipmentNotificationService] Push notification failed (non-blocking):', err.message);
                }
              }
            };
            
            // Fire and forget - don't await
            sendPushNotification();

            console.log('[ShipmentNotificationService] Created notification for sales rep:', salesRep.id);
          } catch (error: any) {
            console.error('[ShipmentNotificationService] Error creating notification:', {
              salesRepId: salesRep.id,
              error: error.message
            });
            // Don't throw - notification failure shouldn't break shipment assignment
          }

          salesRepIds.push(salesRep.id);
        } catch (error: any) {
          console.error('[ShipmentNotificationService] Error sending to sales rep:', {
            salesRepId: salesRep.id,
            error: error.message
          });
          // Continue with other sales reps even if one fails
        }
      }

      // Mark shipments as sent
      const shipmentIds = shipments.map(s => s.id);
      await ShipmentService.markAsSentToSalesReps(shipmentIds, managerId, salesRepIds);

      console.log('[ShipmentNotificationService] Successfully sent shipments to', salesRepIds.length, 'sales reps');
    } catch (error: any) {
      console.error('[ShipmentNotificationService] Error sending shipments to sales reps:', {
        error: error.message,
        stack: error.stack,
        salesGroup,
        managerId
      });
      throw error;
    }
  }

  /**
   * Get sales reps by sales group
   * 
   * @param salesGroup - Sales group identifier
   * @returns Promise resolving to array of sales rep user objects
   */
  private static async getSalesRepsBySalesGroup(salesGroup: string): Promise<Array<{ id: string; email?: string; name?: string }>> {
    try {
      const q = query(
        collection(db, this.USERS_COLLECTION),
        where('salesGroup', '==', salesGroup),
        where('role', '==', 'sales-representative')
      );

      const snapshot = await getDocs(q);
      const salesReps: Array<{ id: string; email?: string; name?: string }> = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        salesReps.push({
          id: doc.id,
          email: data.email || data.User_mail,
          name: data.username || data.name
        });
      });

      return salesReps;
    } catch (error: any) {
      console.error('[ShipmentNotificationService] Error getting sales reps:', error);
      throw error;
    }
  }

  /**
   * Generate email content for shipments
   * 
   * @param shipmentGroups - Shipment groups (grouped by tracking ID)
   * @param salesGroup - Sales group identifier
   * @returns HTML email content
   */
  private static generateEmailContent(shipmentGroups: ShipmentGroup[], salesGroup: string): string {
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 800px; margin: 0 auto; padding: 20px; }
          .header { background-color: #4CAF50; color: white; padding: 20px; border-radius: 5px 5px 0 0; }
          .content { background-color: #f9f9f9; padding: 20px; border: 1px solid #ddd; }
          .shipment-group { background-color: white; margin: 15px 0; padding: 15px; border-radius: 5px; border-left: 4px solid #4CAF50; }
          .shipment-header { font-weight: bold; font-size: 16px; margin-bottom: 10px; }
          .tracking-link { display: inline-block; background-color: #4CAF50; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; margin: 10px 0; }
          .shipment-details { margin: 10px 0; }
          .shipment-details p { margin: 5px 0; }
          .items-list { margin-top: 10px; }
          .item { padding: 5px; background-color: #f5f5f5; margin: 5px 0; border-radius: 3px; }
          .footer { text-align: center; margin-top: 20px; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>New Shipments Assigned</h1>
            <p>Sales Group: ${salesGroup}</p>
          </div>
          <div class="content">
            <p>You have been assigned ${shipmentGroups.length} shipment(s). Please find the details below:</p>
    `;

    for (const group of shipmentGroups) {
      const carrierName = TrackingLinkService.getCarrierDisplayName(group.carrier);
      
      html += `
        <div class="shipment-group">
          <div class="shipment-header">
            Shipment to: ${group.shipToName}
          </div>
          <div class="shipment-details">
            <p><strong>Tracking Number:</strong> ${group.trackingId}</p>
            <p><strong>Carrier:</strong> ${carrierName}</p>
            <p><strong>Ship Date:</strong> ${group.shipDate}</p>
            <p><strong>Address:</strong> ${group.shipToAddress}, ${group.shipToCity}, ${group.shipToState}</p>
            <p><strong>Total Items:</strong> ${group.totalItems}</p>
            <p><strong>Total Quantity:</strong> ${group.totalQuantity}</p>
      `;

      if (group.trackingUrl) {
        html += `
          <a href="${group.trackingUrl}" class="tracking-link" target="_blank">
            Track Shipment
          </a>
        `;
      }

      html += `
          <div class="items-list">
            <strong>Items in this shipment:</strong>
      `;

      for (const shipment of group.shipments) {
        html += `
          <div class="item">
            ${shipment.itemCodeDesc || shipment.itemCode || 'N/A'} 
            (Qty: ${shipment.quantityShipped})
          </div>
        `;
      }

      html += `
          </div>
        </div>
      </div>
      `;
    }

    html += `
          </div>
          <div class="footer">
            <p>This is an automated notification from McKenzie Portal.</p>
            <p>Please contact your manager if you have any questions.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    return html;
  }

  /**
   * Create in-app notification
   * 
   * @param shipmentId - Shipment ID
   * @param salesRepId - Sales rep user ID
   * @param managerId - Manager user ID who sent
   */
  static async createInAppNotification(
    shipmentId: string,
    salesRepId: string,
    managerId: string
  ): Promise<void> {
    try {
      const notificationRef = doc(collection(db, this.NOTIFICATIONS_COLLECTION));
      const notification: Omit<ShipmentNotification, 'id'> = {
        shipmentId,
        sentTo: salesRepId,
        sentAt: Timestamp.now(),
        sentBy: managerId,
        notificationType: 'in_app',
        read: false
      };

      await setDoc(notificationRef, notification);
      console.log('[ShipmentNotificationService] Created in-app notification:', notificationRef.id);
    } catch (error: any) {
      console.error('[ShipmentNotificationService] Error creating in-app notification:', error);
      throw error;
    }
  }

  /**
   * Get notifications for a user
   * 
   * @param userId - User ID
   * @returns Promise resolving to array of notifications
   */
  static async getNotifications(userId: string): Promise<ShipmentNotification[]> {
    try {
      const q = query(
        collection(db, this.NOTIFICATIONS_COLLECTION),
        where('sentTo', '==', userId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      const notifications: ShipmentNotification[] = [];

      snapshot.forEach((doc) => {
        notifications.push({
          id: doc.id,
          ...doc.data()
        } as ShipmentNotification);
      });

      return notifications;
    } catch (error: any) {
      console.error('[ShipmentNotificationService] Error getting notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   * 
   * @param notificationId - Notification ID
   */
  static async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, this.NOTIFICATIONS_COLLECTION, notificationId);
      await setDoc(notificationRef, {
        read: true,
        readAt: Timestamp.now()
      }, { merge: true });
    } catch (error: any) {
      console.error('[ShipmentNotificationService] Error marking notification as read:', error);
      throw error;
    }
  }
}

